/**
 * The opportunity funnel (PRD 10.4; Phase 33): what the HubSpot pipeline would ask of us, by
 * stage and by service, weighted and unweighted, and which week each part of it lands on.
 *
 * Every other view draws the pipeline as one layer — a band in the ribbon, a line over the
 * bench, a share of the bridge. This one opens that layer up: the deals inside it, grouped the
 * way a sales conversation groups them, with the win rate each stage's weighting came from
 * beside the hours it produced. The PRD's sketch is four columns — *stage, weighted hours, peak
 * week impact, historical win rate* — and the whole of this module is the arithmetic behind
 * those four and the two toggles (by stage or by service, weighted or unweighted).
 *
 * Four rules live here, shared by the read model behind `GET /forecast/funnel`, the card and the
 * tests, so none of them can hold a different opinion of what a funnel row is:
 *
 * - **Both modes are always present; the toggle chooses which one is read.** A row carries its
 *   weighted and its unweighted hours together, because the two are the same deals at two
 *   confidences and the question "what if they all land" is one click from "what we expect".
 *   The endpoint never returns one mode alone, so the toggle costs no request and the two can
 *   be drawn at once: the unweighted bar is the track and the weighted bar is nested inside it.
 * - **The nesting is the funnel.** A row's weighted hours over its unweighted hours is the
 *   probability the run actually used, blended over the row's deals by hours. It is not read
 *   from a setting or recomputed here: it is the ratio of two figures the run wrote, which is
 *   why the nested bar cannot disagree with the numbers beside it.
 * - **A row's hue is its size unweighted.** The stack of weeks is hued by row, and the rank
 *   that picks the hues is the row's unweighted hours — the mode-independent size — so hues do
 *   not swap when the reader toggles the mode.
 * - **Peak week impact is against capacity, not against the pipeline.** The peak week of a row
 *   is the week it puts the most hours in, and its impact is those hours over that week's
 *   capacity: the PRD's "Wk 46 (+9%)" is nine per cent of the company's week, not nine per cent
 *   of the pipeline. A week with no capacity has no impact, never a division by zero.
 *
 * Pure: no DOM, no I/O.
 */
import { MAX_TYPE_HUES, OTHER_SLOT, assignTypePalette } from './palette.js';
import { col } from './table.js';

/** The shared filters this view reads, in the API's names. */
export const FUNNEL_PARAM_KEYS = Object.freeze(['run_id', 'from', 'weeks', 'dimension', 'entity']);

/** The two groupings of PRD 10.4: "view by stage" and "view by service impact". */
export const FUNNEL_GROUPINGS = Object.freeze(['stage', 'service']);

/**
 * The two modes a funnel ROW can be read in: a row carries hours twice, once weighted and once
 * at full value, and the toggle chooses which of the two figures is read. `none` is not one of
 * them, because a funnel with the opportunities excluded is an empty screen rather than a
 * different reading.
 */
export const FUNNEL_MODES = Object.freeze(['weighted', 'unweighted']);

/**
 * The three modes the toggle OFFERS (PRD 10.4: "view weighted vs unweighted vs sampled (fan
 * chart with P10-P90 band)"), which is deliberately not the same list.
 *
 * `sampled` is not a third set of hours a row could be read in — a stage is not a dimension a
 * deal's hours were sampled in, and a percentile is not a sum, so there is no such thing as
 * "Proposal's P90". It is a different *question* about the same pipeline, asked of the week
 * rather than of the row: how many of the futures put this week over the line. So the funnel's
 * rows stay weighted under it (`readMode` maps anything unknown to weighted, which is also what
 * `pipelineReadMode` does for every row-based read) and the weekly chart beneath them is
 * replaced by the fan (Phase 40, `lib/fan.js`), which draws the band the samples hold.
 */
export const FUNNEL_TOGGLE_MODES = Object.freeze(['weighted', 'unweighted', 'sampled']);

/** Whether the reader has asked for the band rather than for one of the two point estimates. */
export function isSampled(mode) {
  return mode === 'sampled';
}

/** What the toggle's buttons are called. */
export function toggleModeLabel(mode) {
  if (mode === 'sampled') return 'Sampled';
  return mode === 'unweighted' ? 'Unweighted' : 'Weighted';
}

/**
 * The dimensions that mean anything here. A pipeline row names no person, no department and no
 * office (`user_id` NULL, `department` and `office` NULL: see `forecast/pipeline/README.md`), so
 * the only narrowing a deal can carry is the service its hours were split into. Any other
 * dimension would return an empty funnel and read as a bug; the card drops it and says so.
 */
export const FUNNEL_DIMENSIONS = Object.freeze(['company', 'service']);

/** How many rows take a hue of their own in the weekly stack before the rest fold into one. */
export const MAX_ROW_HUES = MAX_TYPE_HUES;

/** The folded segment: every row past the hued ones, drawn once in the neutral slot. */
export const OTHER_ROWS = Object.freeze({
  key: 'other_rows',
  label: 'Other',
  token: OTHER_SLOT.var,
});

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const round4 = (n) => Math.round((n + Number.EPSILON) * 10000) / 10000;
const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const pct = (value, max) => (max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0);

/** The mode a request or a reader asked for, defaulted to weighted. */
export function readMode(mode) {
  return FUNNEL_MODES.includes(mode) ? mode : 'weighted';
}

/** A row's hours in one mode. */
export function modeHours(row, mode) {
  return round2(num(readMode(mode) === 'unweighted' ? row?.hours_unweighted : row?.hours_weighted));
}

/** A row's peak week in one mode, or null where the row has no hours in the window. */
export function modePeak(row, mode) {
  return (readMode(mode) === 'unweighted' ? row?.peak_unweighted : row?.peak_weighted) ?? null;
}

/**
 * The probability the run used, blended over a row's deals by hours: weighted over unweighted.
 * Null where the row has no unweighted hours to divide by.
 */
export function blendedProbability(row) {
  const full = num(row?.hours_unweighted);
  if (full <= 0) return null;
  return round4(num(row?.hours_weighted) / full);
}

/** Hours as a share of a week's capacity: the PRD's "+9%". Null where the week has no capacity. */
export function impact(hours, capacityHours) {
  const capacity = num(capacityHours);
  if (capacity <= 0) return null;
  return round4(num(hours) / capacity);
}

/**
 * The order the rows are drawn in.
 *
 * By stage, a funnel is a sequence and the pipeline's own order is the sequence: Discovery above
 * Proposal above Verbal, as the PRD sketch has it, whatever hours each carries. Sorting a funnel
 * by size would destroy the one thing its shape says — that deals move down it. By service there
 * is no sequence, so the largest goes first (unweighted, the mode-independent size).
 *
 * @param {Array<object>} rows
 * @param {'stage' | 'service'} group
 */
export function orderRows(rows, group = 'stage') {
  const list = [...(rows ?? [])].map((r) => ({ ...r, label: r.label ?? r.key }));
  const byOrder = (a, b) => {
    const ao = a.stage?.display_order;
    const bo = b.stage?.display_order;
    if (ao !== null && ao !== undefined && bo !== null && bo !== undefined && ao !== bo)
      return ao - bo;
    if ((ao ?? null) === null && (bo ?? null) !== null) return 1;
    if ((ao ?? null) !== null && (bo ?? null) === null) return -1;
    return a.label.localeCompare(b.label) || a.key.localeCompare(b.key);
  };
  const bySize = (a, b) =>
    num(b.hours_unweighted) - num(a.hours_unweighted) ||
    a.label.localeCompare(b.label) ||
    a.key.localeCompare(b.key);
  return list.sort(group === 'stage' ? byOrder : bySize).map((r, i) => ({ ...r, position: i + 1 }));
}

/**
 * Rows ranked by size for the hue assignment: unweighted hours, ties by label then key. This is
 * deliberately not the drawing order — a stage funnel is drawn in pipeline order and hued by
 * size, so the hues stay put when the reader toggles the mode or the grouping's order changes.
 */
export function rankRows(rows) {
  return [...(rows ?? [])]
    .map((r) => ({ ...r, label: r.label ?? r.key }))
    .sort(
      (a, b) =>
        num(b.hours_unweighted) - num(a.hours_unweighted) ||
        a.label.localeCompare(b.label) ||
        a.key.localeCompare(b.key),
    )
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

/**
 * The hue each row wears in the weekly stack: the first `MAX_ROW_HUES` by unweighted hours take
 * the type slots in slot order, the rest read as Other. The palette's own assignment, so the
 * adjacent-pair validation of the slot order carries over.
 * @returns {Map<string, { slot: number, hue: string, token: string, rank: number }>}
 */
export function rowSlots(rows) {
  const ranked = rankRows(rows);
  const assigned = assignTypePalette(
    ranked.map((r) => ({
      value: r.key,
      label: r.label,
      volume: num(r.hours_unweighted),
      priority: 1,
    })),
  );
  const out = new Map();
  for (const a of assigned) {
    const rank = ranked.find((r) => r.key === a.value)?.rank ?? ranked.length + 1;
    out.set(a.value, { slot: a.slot, hue: a.hue, token: a.var, rank });
  }
  return out;
}

/**
 * The segments the weekly stack is drawn in, bottom-up: the hued rows in the drawing order of
 * the grouping, then one folded segment for the rest (absent when every row has its own hue).
 *
 * @param {Array<object>} rows
 * @param {'stage' | 'service'} group
 */
export function foldRows(rows, group = 'stage') {
  const slots = rowSlots(rows);
  const drawn = [];
  const folded = [];
  for (const r of orderRows(rows, group)) {
    const s = slots.get(r.key);
    if (s && s.slot <= MAX_ROW_HUES) drawn.push({ ...r, token: s.token, rank: s.rank });
    else folded.push(r);
  }
  return { drawn, other: folded.length ? { ...OTHER_ROWS, rows: folded } : null };
}

/** The segments in drawing order, the folded one last. */
export function segmentsOf(fold) {
  return [...(fold?.drawn ?? []), ...(fold?.other ? [fold.other] : [])];
}

/**
 * One week's stack in one mode: `[from, to]` per segment in drawing order, the folded rows
 * summed into their one segment. The segments sum to the week's hours in that mode.
 *
 * @param {Record<string, { weighted: number, unweighted: number }>} weekRows  `weeks[].rows`
 * @param {ReturnType<typeof foldRows>} fold
 * @param {'weighted' | 'unweighted'} mode
 */
export function weekStack(weekRows, fold, mode) {
  const picked = readMode(mode);
  const hoursOf = (key) => round2(num(weekRows?.[key]?.[picked]));
  let base = 0;
  const segment = (key, label, token, hours, extra = {}) => {
    const s = { key, label, token, from: base, to: round2(base + hours), hours, ...extra };
    base = s.to;
    return s;
  };
  const segments = (fold?.drawn ?? []).map((r) => segment(r.key, r.label, r.token, hoursOf(r.key)));
  if (fold?.other) {
    const parts = fold.other.rows
      .map((r) => ({ key: r.key, label: r.label, hours: hoursOf(r.key) }))
      .filter((p) => p.hours > 0);
    segments.push(
      segment(
        fold.other.key,
        fold.other.label,
        fold.other.token,
        round2(parts.reduce((s, p) => s + p.hours, 0)),
        { rows: parts },
      ),
    );
  }
  return segments;
}

/** The top of the funnel's horizontal axis: the largest row, always unweighted, so the nested
 *  weighted bar is read against the same axis the unweighted track is. */
export function funnelScaleMax(rows) {
  let max = 0;
  for (const r of rows ?? []) max = Math.max(max, num(r.hours_unweighted));
  return max;
}

/**
 * Where a row's two bars sit on the shared axis, as percentages of it: the unweighted track,
 * the weighted bar nested inside it, and which of the two the reader is reading.
 *
 * @param {object} row
 * @param {number} max  `funnelScaleMax` of the whole response, never of a filtered subset
 * @param {'weighted' | 'unweighted'} mode
 */
export function funnelGeometry(row, max, mode) {
  const full = round2(num(row?.hours_unweighted));
  const weighted = round2(num(row?.hours_weighted));
  return {
    track: pct(full, max),
    bar: pct(weighted, max),
    read: readMode(mode) === 'unweighted' ? pct(full, max) : pct(weighted, max),
    probability: blendedProbability(row),
  };
}

/**
 * The top of the weekly chart's value axis: the largest week in the mode.
 *
 * Deliberately not the capacity line. The pipeline is a fraction of a week's capacity — a
 * healthy one is well under a fifth of it — so an axis that reached the capacity line would
 * flatten every bar to a sliver and hide the shape the view exists to show. Capacity is not
 * dropped, it is stated the other way round: each week carries its share of capacity as the
 * impact figure, in the hover and beside the peak, which is the PRD's "+9%" and the one
 * number the axis would have made unreadable. One value axis, hours, as PRD 7.3 requires.
 */
export function weekScaleMax(weeks, mode) {
  const picked = readMode(mode);
  let max = 0;
  for (const w of weeks ?? [])
    max = Math.max(max, num(picked === 'unweighted' ? w.unweighted_hours : w.weighted_hours));
  return max;
}

/** What the funnel's value is called once the mode is known. */
export function modeLabel(mode) {
  return readMode(mode) === 'unweighted' ? 'Unweighted hours' : 'Weighted hours';
}

/**
 * The population beneath a row's win rate: the closed deals it was learned from, and whether
 * the run could use it. Null where the row has no population of its own — a service is not a
 * step a deal passes through — in which case the figure above it is the blended probability.
 */
export function winRateSample(row) {
  if (row?.win_rate === null || row?.win_rate === undefined) return null;
  const sample = `${row.deals_won ?? 0} of ${row.deals_reached ?? 0} closed deals`;
  return row.sample_sufficient ? sample : `${sample}, below the sample minimum`;
}

/**
 * What a row's win rate column says, in one line. A stage row shows the calibrated population
 * (PRD 10.4's "Hist. Win Rate", Phase 13's `forecast_stage_calibration`); a service row shows
 * the blended probability the run's weighting actually used instead, named as such. The card
 * splits the same two pieces over two lines rather than wording them again.
 */
export function winRateText(row, { percent = (v) => `${Math.round(v * 100)}%` } = {}) {
  const sample = winRateSample(row);
  if (sample !== null) return `${percent(row.win_rate)} (${sample})`;
  const blended = blendedProbability(row);
  if (blended === null) return 'No win rate';
  return `${percent(blended)} blended`;
}

/** The PRD's "Wk 46 (+9%)" for one row in one mode. */
export function peakText(
  row,
  mode,
  { hours = (h) => `${h} h`, week = (iso) => iso, percent = (v) => `${Math.round(v * 100)}%` } = {},
) {
  const peak = modePeak(row, mode);
  if (!peak) return 'No hours in this window';
  const share = peak.impact === null ? 'no capacity that week' : `+${percent(peak.impact)}`;
  return `${week(peak.week_start)} (${share}, ${hours(peak.hours)})`;
}

/** The headline figures of a response in one mode, for the card's KPIs and the tests. */
export function funnelSummary(data, mode) {
  const picked = readMode(mode);
  const t = data?.totals ?? {};
  const rows = data?.rows ?? [];
  const hours = round2(num(picked === 'unweighted' ? t.hours_unweighted : t.hours_weighted));
  const peak = picked === 'unweighted' ? (t.peak_unweighted ?? null) : (t.peak_weighted ?? null);
  return {
    mode: picked,
    hours,
    hours_weighted: round2(num(t.hours_weighted)),
    hours_unweighted: round2(num(t.hours_unweighted)),
    blended_probability: blendedProbability(t),
    capacity_hours: round2(num(t.capacity_hours)),
    share_of_capacity: impact(hours, t.capacity_hours),
    deals: Number(t.deals ?? 0),
    rows: rows.length,
    weeks: (data?.weeks ?? []).length,
    peak,
    excluded_deals: Number(t.excluded_deals ?? 0),
  };
}

/** One row per funnel row for the table and the CSV, in drawing order. */
export function funnelExportRows(data, group, mode) {
  const picked = readMode(mode);
  return orderRows(data?.rows ?? [], group ?? data?.group).map((r) => {
    const peak = modePeak(r, picked);
    return {
      position: r.position,
      key: r.key,
      label: r.label,
      planning_category: r.stage?.planning_category ?? null,
      deals: Number(r.deals ?? 0),
      hours_weighted: round2(num(r.hours_weighted)),
      hours_unweighted: round2(num(r.hours_unweighted)),
      blended_probability: blendedProbability(r),
      peak_week: peak?.week_start ?? null,
      peak_hours: peak ? round2(num(peak.hours)) : null,
      peak_impact: peak?.impact ?? null,
      win_rate: r.win_rate ?? null,
      deals_reached: r.deals_reached ?? null,
      deals_won: r.deals_won ?? null,
      sample_sufficient: r.sample_sufficient ?? null,
      share: impact(
        picked === 'unweighted' ? r.hours_unweighted : r.hours_weighted,
        picked === 'unweighted' ? data?.totals?.hours_unweighted : data?.totals?.hours_weighted,
      ),
    };
  });
}

/** One row per week for the table and the CSV: the drawn segments as columns, then the totals. */
export function weekExportRows(data, fold, mode) {
  const picked = readMode(mode);
  return (data?.weeks ?? []).map((w) => {
    const stack = weekStack(w.rows, fold, picked);
    return {
      week_start: w.week_start,
      week_end: w.week_end,
      ...Object.fromEntries(stack.map((s) => [`row_${s.key}`, s.hours])),
      hours_weighted: round2(num(w.weighted_hours)),
      hours_unweighted: round2(num(w.unweighted_hours)),
      capacity_hours: round2(num(w.capacity_hours)),
      committed_hours: round2(num(w.committed_hours)),
      impact: impact(
        picked === 'unweighted' ? w.unweighted_hours : w.weighted_hours,
        w.capacity_hours,
      ),
      deals: Number(w.deals ?? 0),
    };
  });
}

/** One row per deal per funnel row for the table and the CSV: what a row's total is made of. */
export function dealExportRows(data, mode) {
  const picked = readMode(mode);
  return [...(data?.deals ?? [])]
    .map((d) => ({
      ...d,
      hours: round2(num(picked === 'unweighted' ? d.hours_unweighted : d.hours_weighted)),
    }))
    .sort(
      (a, b) =>
        b.hours - a.hours ||
        (a.name ?? '').localeCompare(b.name ?? '') ||
        a.row.localeCompare(b.row),
    );
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings. The grouping decides what a row is called, so each takes it.

/** One row per stage or service. */
export function funnelRowColumns({ group = 'stage' } = {}) {
  return [
    col.count('position', '#'),
    col.text('label', group === 'stage' ? 'Stage' : 'Service'),
    col.text('planning_category', 'Category'),
    col.count('deals', 'Deals'),
    col.hours('hours_weighted', 'Weighted hours'),
    col.hours('hours_unweighted', 'Unweighted hours'),
    col.percent('blended_probability', 'Blended probability'),
    col.week('peak_week', 'Peak week'),
    col.hours('peak_hours', 'Peak hours'),
    col.percent('peak_impact', 'Peak week impact'),
    col.percent('win_rate', 'Hist. win rate'),
    col.count('deals_reached', 'Deals reached'),
    col.count('deals_won', 'Deals won'),
    col.percent('share', 'Share of the total'),
  ];
}

/**
 * One row per week. `segments` are the stacked bands, in the order they are drawn.
 * @param {{ segments?: {key: string, label: string}[] }} opts
 */
export function funnelWeekColumns({ segments = [] } = {}) {
  return [
    col.week('week_start', 'Week'),
    ...segments.map((seg) => col.hours(`row_${seg.key}`, seg.label)),
    col.hours('hours_weighted', 'Weighted hours'),
    col.hours('hours_unweighted', 'Unweighted hours'),
    col.percent('impact', 'Share of capacity'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('committed_hours', 'Already committed'),
    col.count('deals', 'Deals'),
  ];
}

/** One row per deal. */
export function funnelDealColumns({ group = 'stage' } = {}) {
  return [
    col.text('name', 'Deal'),
    col.text('client', 'Client'),
    col.text('row_label', group === 'stage' ? 'Stage' : 'Service'),
    col.hours('hours_weighted', 'Weighted hours'),
    col.hours('hours_unweighted', 'Unweighted hours'),
    col.percent('probability_used', 'Probability used'),
    col.text('probability_source', 'From'),
    col.week('close_date_adjusted', 'Close (adjusted)'),
    col.week('window_start', 'Window'),
    col.text('scope_tier', 'Tier'),
    col.text('source_deal_id', 'Deal id'),
  ];
}
